/**
 * /dashboard/accounting/sales/invoices/$id — Invoice detail (G_ACC5).
 *
 * Routes to InvoiceDetail which manages its own edit-mode pivot
 * (DRAFT only). Read-only otherwise.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/sales/invoices/[id]/page.tsx
 * with `use(params)` → Route.useParams() and `next/link` → tanstack Link.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useInvoice } from "@/hooks/use-accounting";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { InvoiceDetail } from "@/components/accounting/invoice-detail";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/sales/invoices/$id",
)({
  component: InvoiceDetailRoute,
});

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

function InvoiceDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const q = useInvoice(numId, validId);

  return (
    <AccountingModuleShell
      title="Invoice"
      description={q.data?.invoice_no}
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/accounting/sales" params={{ tenantSlug }} search={{ tab: "invoices" }}>
            <ArrowLeft aria-hidden className="h-4 w-4" />
            Back to sales
          </Link>
        </Button>
      }
    >
      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid invoice id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid invoice id.
          </AlertDescription>
        </Alert>
      )}

      {validId && q.isLoading && <DetailSkeleton />}

      {validId && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this invoice</AlertTitle>
          <AlertDescription>
            {toApiError(q.error).message}{" "}
            <Link
              to="/app/$tenantSlug/accounting/sales" params={{ tenantSlug }}
              search={{ tab: "invoices" }}
              className="ml-1 inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to invoices
              <ChevronRight aria-hidden className="h-4 w-4" />
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {validId && q.data && <InvoiceDetail invoice={q.data} />}
    </AccountingModuleShell>
  );
}
